import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime, filter, takeUntil, tap } from 'rxjs/operators';
import { Project } from 'app/modules/projects/projects.types';
import { ProjectsService } from 'app/modules/projects/projects.service';
import { AccountService } from 'app/core';
import { Account } from 'app/core/models/account.types';
import { FoldersTabService } from 'app/shared/modules/folders/folders.service';

import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { ENTER } from '@angular/cdk/keycodes';
import { MatLegacyMenu as MatMenu } from '@angular/material/legacy-menu';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { FileResource } from 'app/shared/modules/folders/folders.types';
import { MatDrawerToggleResult } from '@angular/material/sidenav';
import { LoansService } from '../loans.service';
import { LoanRequest, LoanQuote } from '../loans.types';
import { LoanRequestDetailsComponent } from '../details/details.component';
import { ConfirmService } from 'app/shared/components/widgets/dialog/confirm/confirm.service';
import { Store } from '@ngxs/store';
import { AccountState } from '@state/account/state';
import { FeatureFlag, NewPermission } from '@core/enums';
import { UserState } from '@state/user/state';

@Component({
  selector: 'project-loan-quote-details',
  templateUrl: './loan-quote-details.component.html',
  styleUrls: ['./loan-quote-details.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoanQuoteDetailsComponent implements OnInit, AfterViewInit, OnDestroy {
  account: Account;
  loanRequest: LoanRequest;
  loanQuote: LoanQuote;
  loanQuoteForm: UntypedFormGroup;
  separatorKeysCodes: number[] = [ENTER];
  private _unsubscribeAll: Subject<void>;
  project: Project;

  @ViewChild('matMenu', { static: true }) matMenu: MatMenu;

  constructor(
    private _activatedRoute: ActivatedRoute,
    private _changeDetectorRef: ChangeDetectorRef,
    private _formBuilder: UntypedFormBuilder,
    private _router: Router,
    private _snackBar: MatSnackBar,
    private _accountService: AccountService,
    private _projectsService: ProjectsService,
    private _foldersTabService: FoldersTabService,
    private _loansService: LoansService,
    private _loanRequestDetailsComponent: LoanRequestDetailsComponent,
    private confirmService: ConfirmService,
    private readonly store: Store,
  ) {
    this._unsubscribeAll = new Subject();
  }

  ngOnInit(): void {
    // Open the drawer
    this._loanRequestDetailsComponent.matDrawer.open();

    // Create the task form
    this.loanQuoteForm = this._formBuilder.group(
      {
        id: [''],
        description: [''],
      },
      {
        updateOn: 'blur',
      },
    );

    this._accountService.currentAccount.subscribe((accountData) => {
      this.account = accountData;
      this._foldersTabService.accountId = accountData.id;
    });

    this._projectsService.project$.subscribe((project: Project) => {
      this.project = project;
    });

    this._loansService.loanRequest$.pipe(takeUntil(this._unsubscribeAll)).subscribe((loanRequest: LoanRequest) => {
      if (loanRequest) {
        this.loanRequest = loanRequest;
      }

      this._changeDetectorRef.markForCheck();
    });

    // Get the task
    this._loansService.loanQuote$.pipe(takeUntil(this._unsubscribeAll)).subscribe((loanQuote: LoanQuote) => {
      // Get the task
      this.loanQuote = loanQuote;

      // Patch values to the form from the task
      this.loanQuoteForm.patchValue(loanQuote, { emitEvent: false });

      // Mark for check
      this._changeDetectorRef.markForCheck();
    });

    this.loanQuoteForm
      .get('description')
      .valueChanges.pipe(
        tap((value) => {
          this.loanQuote.description = value;
        }),
        debounceTime(300),
        takeUntil(this._unsubscribeAll),
      )
      .subscribe((value) => {
        // Update the task on the server
        this._loansService.updateLoanRequote(this.loanRequest.id, this.loanQuote).subscribe();

        // Mark for check
        this._changeDetectorRef.markForCheck();
      });

    // Listen for NavigationEnd event to focus on the title field
    this._router.events
      .pipe(
        takeUntil(this._unsubscribeAll),
        filter((event) => event instanceof NavigationEnd),
      )
      .subscribe(() => {
        // Focus on the title field
        //this._titleField.nativeElement.focus();
      });
  }

  /**
   * After view init
   */
  ngAfterViewInit(): void {
    // Listen for matDrawer opened change
    this._loanRequestDetailsComponent.matDrawer.openedChange
      .pipe(
        takeUntil(this._unsubscribeAll),
        filter((opened) => opened),
      )
      .subscribe(() => {
        // Focus on the title element
        //this._titleField.nativeElement.focus();
      });
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions

    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  loanLoanQuote(): void {
    this._loansService.getLoanQuoteById(this.loanQuote.id.toString()).subscribe();
  }

  onFileChanged($event) {
    let fileToUpload = $event.target.files[0];
    let fileResource: FileResource = {};
    fileResource.folder_resource_id = this.project.root_folder_resource_id;
    fileResource.name = fileToUpload.name;
    fileResource.related_to_type = 'LoanQuote';
    fileResource.related_to_id = this.loanQuote.id;
    this._foldersTabService.moduleId = this.project.id;
    this._foldersTabService.moduleBaseUrl = 'projects';

    this._foldersTabService.createFile(fileResource.folder_resource_id, fileResource).subscribe((fileResource) => {
      this._foldersTabService
        .uploadDocument(fileResource.folder_resource_id, fileResource.id, fileToUpload)
        .subscribe((document) => {
          this.loanLoanQuote();
        });
    });
  }

  downloadDocument(fileResource): void {
    if (fileResource.documents.length > 0) {
      let document = fileResource.documents[0];
      window.open(document.original_url, '_blank');
    }
  }

  deleteFile(fileResource): void {
    this.confirmService
      .openConfirmationModal()
      .pipe(filter((res: boolean) => res))
      .subscribe(() => {
        this._foldersTabService.deleteFile(fileResource.folder_resource_id, fileResource.id).subscribe((response) => {
          this.loanLoanQuote();
        });
      });
  }

  /**
   * Close the drawer
   */
  closeDrawer(): Promise<MatDrawerToggleResult> {
    return this._loanRequestDetailsComponent.matDrawer.close();
  }

  /**
   * Delete the task
   */
  deletePropertyUnit(): void {
    // Get the current task's id
    const id = this.loanQuote.id;

    // Delete the task
    this._loansService.deleteLoanQuote(this.loanRequest.id, id).subscribe((isDeleted) => {
      // Return if the task wasn't deleted...
      //if (!isDeleted) {
      //    return;
      //}

      // Get the current activated route
      let route = this._activatedRoute;
      while (route.firstChild) {
        route = route.firstChild;
      }

      this._router.navigate(['../'], { relativeTo: route });
      this._snackBar.open('Loan quote deleted.', '', {
        duration: 3000,
        horizontalPosition: 'center',
        verticalPosition: 'bottom',
      });
    });
    this._changeDetectorRef.markForCheck();
  }

  /**
   * Track by function for ngFor loops
   *
   * @param index
   * @param item
   */
  trackByFn(index: number, item: any): any {
    return item.id || index;
  }

  canEditLoanQuote(): boolean {
    if (this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NEW_PERMISSIONS))) {
      return this.store.selectSnapshot(UserState.hasPermission(NewPermission.LOAN_QUOTES_EDIT));
    }

    return true;
  }

  canDeleteLoanQuote(): boolean {
    if (this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NEW_PERMISSIONS))) {
      return this.store.selectSnapshot(UserState.hasPermission(NewPermission.LOAN_QUOTES_DELETE));
    }

    return true;
  }
}
